import type { Translation } from '../types.ts';
import type en from '../en/products.ts';

export default {
    shared: {
        sku: 'SKU',
        stock: 'Voorraad',
        variants: 'Varianten',
        variant: 'Variant',
        featured: 'Uitgelicht',
        categories: 'Categorieën',
        details: 'Gegevens',
        visibility: 'Zichtbaarheid',
        general: 'Algemeen',
        priceEx: 'Prijs (excl. btw)',
        priceIncl: 'Prijs (incl. btw)',
        slugHint: 'Deel van de URL; wordt uit de naam afgeleid als je het leeg laat',
        localeSlugHint: 'Deel van de URL voor {locale}; leeg houdt /{slug}',
        localeFallback: 'Laat een veld leeg om de tekst van de standaardtaal te gebruiken.',
        localeDescriptionFallback: 'Leeg = de omschrijving van de standaardtaal wordt gebruikt.',
        replaceImage: 'Afbeelding vervangen',
        noImagePath: '(geen afbeelding)',
        pickImage: 'Kies of upload een afbeelding',
        addValueTo: 'Waarde toevoegen aan {option}',
        noCategories: 'Nog geen categorieën.',
    },
    seo: {
        tab: 'SEO',
        searchEngines: 'Zoekmachines',
        pageTitle: 'Paginatitel',
        pageTitleHint: 'Getoond als titel van het browsertabblad en als kop in de zoekresultaten',
        metaDescription: 'Metabeschrijving',
        metaDescriptionHint: 'De ideale lengte is 120 tot 158 tekens',
        socialTitle: 'Titel voor social media',
        socialTitleHint: 'Open Graph-titel die wordt gebruikt als de pagina wordt gedeeld',
        socialDescription: 'Omschrijving voor social media',
        advanced: 'Geavanceerd',
        sharedByLanguages: 'Geldt voor alle talen.',
        socialImage: 'Afbeelding voor social media',
        canonicalOverride: 'Afwijkende canonieke URL',
        robots: 'Robots',
        robotsDefault: 'Leeg = de standaard van de website (index,follow).',
        intro: 'Laat een veld leeg voor de standaardwaarde: de productnaam als titel, de korte omschrijving als metabeschrijving.',
        localeFallback: 'Leeg = de waarde van {locale}, daarna de eigen naam van het product in deze taal.',
        socialImageHint: 'Leeg = de eerste afbeelding van het product',
        robotsSiteNoindex: 'De website is verborgen voor zoekmachines, dus dit product stuurt noindex,nofollow, wat hier ook staat. Je wijzigt dit onder Website → Algemeen → Zoekmachines.',
    },
    list: {
        title: 'Producten',
        importCsv: 'CSV importeren',
        newProduct: 'Nieuw product',
        searchPlaceholder: 'Zoek op naam of SKU…',
        allCategories: 'Alle categorieën',
        anyStock: 'Alle voorraad',
        outOfStock: 'Uitverkocht',
        empty: 'Geen producten gevonden met deze filters.',
        priceEx: '{price} excl. btw',
        priceIncl: '{price} incl. btw',
        variantCount: '{n} variant | {n} varianten',
        stockOf: 'Voorraad van {name}',
        stockOfVariant: 'Voorraad van {name}, {variant}',
        live: 'Live',
        hidden: 'Verborgen',
        total: 'geen producten | {n} product | {n} producten',
        perPage: '{select} per pagina',
        pageOf: 'Pagina {page} van {total}',
    },
    edit: {
        newProduct: 'Nieuw product',
        created: 'Product aangemaakt',
        confirmDelete: 'Product "{name}" verwijderen? Dit kan niet ongedaan worden gemaakt.',
        tabs: {
            pricing: 'Prijs',
            stock: 'Voorraad en bestellen',
            images: 'Afbeeldingen',
            organisation: 'Indeling',
        },
        skuHint: 'Uniek binnen deze website',
        brand: 'Merk',
        brandHint: 'Leeg = het standaardmerk van de winkel',
        gtin: 'Barcode (EAN / GTIN)',
        gtinHint: '8, 12, 13 of 14 cijfers, zoals onder de streepjes',
        shortDescription: 'Korte omschrijving',
        access: 'Toegang',
        imagesIntro: 'De eerste afbeelding is de hoofdafbeelding. Klik op een afbeelding om die te vervangen; met de pijlen wijzig je de volgorde.',
        altText: 'Alt-tekst',
        altPlaceholder: 'Beschrijf de afbeelding',
        addImage: '+ Afbeelding toevoegen',
        pricing: 'Prijs',
        priceEntry: 'Prijs invoeren',
        exVat: 'Excl. btw',
        inclVat: 'Incl. btw',
        currency: 'Valuta',
        priceSummaryGross: 'Excl. btw: {net} ({rate}% btw: {tax})',
        priceSummaryNet: 'Incl. {rate}% btw: {gross}',
        taxClass: 'Btw-klasse',
        grossKeepsNote: 'De prijs incl. btw blijft zoals ingevoerd als het tarief verandert; de prijs excl. btw verschuift.',
        vatCategory: 'Btw-categorie',
        followsTaxClass: 'Volgt de btw-klasse',
        checkedFor: 'Gecontroleerd voor:',
        inventory: 'Voorraad',
        giftCardNoStock: 'Voor cadeaubonnen wordt geen voorraad bijgehouden: ze worden op aanvraag aangemaakt, dus een telling zou altijd fout zijn.',
        stockPerVariant: 'De voorraad wordt per variant bijgehouden.',
        editOnVariantsTab: 'Bewerken op het tabblad Varianten',
        trackStock: 'Voorraad bijhouden',
        notTracked: 'Niet bijgehouden: dit product raakt in de winkel nooit uitverkocht.',
        stockFromList: 'Je kunt de voorraad ook direct in de productlijst aanpassen.',
        ordering: 'Bestellen',
        minOrderQuantity: 'Minimale bestelhoeveelheid',
        minOrderQuantityHint: '1 = geen minimum. De winkelwagen weigert kleinere aantallen (bijv. een buffet vanaf 15 personen).',
        weight: 'Gewicht (g)',
        weightHint: 'Gebruikt door de verzendmodule',
        customerNote: 'Notitie van de klant',
        customerNoteSwitch: 'Vraag de klant om een notitie',
        customerNoteIntro: 'Voegt een tekstvak toe aan het bestelformulier van dit product. Wat de klant typt, gaat met de regel in de winkelwagen mee naar de bestelling, de orderbevestiging en de bestelling in de admin.',
        customerNoteLabel: 'Vraag boven het tekstvak',
        customerNotePlaceholder: 'Allergieën of wensen?',
        customerNoteHint: 'Vertaal de vraag per taal met de taaltabbladen hierboven. Hetzelfde product met verschillende notities blijft als aparte regels in de winkelwagen staan.',
        giftCard: 'Cadeaubon',
        giftCardSwitch: 'Dit product is een cadeaubon',
        giftCardIntro: 'Bij aankoop wordt een cadeaubon aangemaakt die de klant bij het afrekenen kan besteden, in plaats van dat er iets wordt verzonden. Geef het product één variant per bedrag dat je wilt aanbieden (25, 50, 100) en de klant kiest er een. Het bestelformulier vraagt dan ook voor wie de bon is, met een optioneel bericht en een verzenddatum.',
        giftCardModule: 'De module {module} moet aan staan voor deze website, anders wordt er niets aangemaakt als de bestelling is betaald.',
        giftCardModuleName: 'Cadeaubonnen',
        tags: 'Tags',
        tagsIntro: 'Met tags vinden klanten producten makkelijker. Typ een naam en druk op Enter om een nieuwe tag te maken.',
        variantMode: 'Variantmodus',
        variantModeSwitch: 'Dit product wordt via varianten verkocht',
        variantModeIntro: 'Als dit aan staat, toont de webwinkel een variantkiezer en komen voorraad en prijs van de afzonderlijke varianten. Zet je het uit, dan blijven de varianten bewaard, maar gebruikt de webwinkel weer de eigen prijs en voorraad van het product.',
    },
    variants: {
        intro: 'Leg optietypen vast (Maat, Kleur, …) met hun waarden en maak dan een variant voor elke combinatie. Elke variant heeft een eigen SKU, barcode, prijs en voorraad. De webwinkel toont een keuzelijst op de productpagina.',
        optionsHeading: 'Opties waarin dit product varieert',
        detachOption: 'Deze optie niet meer gebruiken voor dit product',
        deleteValueEverywhere: '"{value}" overal in deze winkel uit {option} verwijderen',
        noOptions: 'Nog geen. Voeg er een toe (Maat voor een shirt, Bedrag voor een cadeaubon) en geef die de waarden die je wilt aanbieden.',
        addOptionType: 'Optietype toevoegen',
        alreadyInShop: 'Al in deze winkel',
        newOptionPlaceholder: 'Of een nieuwe (bijv. Bedrag)',
        generate: 'Genereren',
        addManually: 'Handmatig toevoegen',
        empty: 'Nog geen varianten. Voeg hierboven een optie toe met de waarden die je wilt aanbieden en klik dan op Genereren.',
        generateTitle: 'Varianten genereren',
        generateIntro: 'Vink de optiewaarden aan die je wilt gebruiken. We maken een variant voor elke combinatie ({n} in totaal).',
        defaultPrice: 'Standaardprijs',
        defaultStock: 'Standaardvoorraad',
        generateCount: '{n} genereren',
        order: 'Volgorde',
        dragHint: 'Sleep om de volgorde te wijzigen',
        detachFailed: 'Die optie kon niet worden verwijderd.',
        confirmDeleteValue: 'Deze waarde verwijderen? Varianten met deze waarde raken die kwijt.',
        pickValue: 'Kies minstens één optiewaarde.',
        generated: '{n} variant aangemaakt ({skipped} al aanwezig). | {n} varianten aangemaakt ({skipped} al aanwezig).',
        checkValues: 'Controleer de optiewaarden.',
        saved: '{name} opgeslagen',
        confirmDelete: 'Variant {name} verwijderen?',
        gtin: 'Barcode',
        gtinPlaceholder: 'EAN / GTIN',
    },
    options: {
        title: 'Productopties',
        intro: 'De eigenschappen waarin producten kunnen variëren (Maat, Kleur, Bedrag), gedeeld door de hele winkel. Welke een product gebruikt, kies je bij dat product, onder Varianten.',
        empty: 'Nog geen optietypen. Voeg er hieronder een toe, of doe dat bij Varianten van een product.',
        deleteType: '{name} uit deze winkel verwijderen',
        deleteValue: '"{value}" uit deze winkel verwijderen',
        noValues: 'Nog geen waarden: een product dat deze optie gebruikt, heeft niets aan te bieden.',
        newTypePlaceholder: 'Nieuw optietype (bijv. Maat)',
        addType: 'Type toevoegen',
        confirmDeleteType: '"{name}" en alle waarden ervan uit deze winkel verwijderen?',
        addTypeFailed: 'Dat optietype kon niet worden toegevoegd.',
        deleteTypeFailed: 'Dat optietype kon niet worden verwijderd.',
        addValueFailed: 'Die waarde kon niet worden toegevoegd.',
        deleteValueFailed: 'Die waarde kon niet worden verwijderd.',
    },
    stockInput: {
        invalid: 'De voorraad moet een heel getal zijn, 0 of meer.',
        save: 'Voorraad opslaan',
        edit: '{label} bewerken',
    },
    tagInput: {
        placeholder: 'Typ een tag en druk op Enter…',
    },
    picker: {
        label: 'Product',
        placeholder: 'Producten zoeken…',
        loadFailed: 'Producten laden is mislukt',
        empty: 'Geen producten gevonden.',
        draft: '(concept)',
    },
} satisfies Translation<typeof en>;
